import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  QueryList,
  ViewChildren,
  computed,
  signal
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';

type ProjectCard = {
  id: number;
  title: string;
  imgUrl: string;
  link?: string;
};

type RenderCard = {
  renderId: number;
  project: ProjectCard;
  width: number;
  height: number;
};

@Component({
  selector: 'app-scroll-projects',
  imports: [CommonModule],
  templateUrl: './scroll-projects.html',
  styleUrl: './scroll-projects.scss'
})
export class ScrollProjects implements AfterViewInit, OnDestroy {
  @ViewChildren('cardHitbox')
  private cardHitboxes!: QueryList<ElementRef<HTMLElement>>;

  projects = signal<ProjectCard[]>([
    {
      id: 1,
      title: 'Project 01',
      imgUrl: '/projects/1.png',
      link: '/project/project-01'
    },
    {
      id: 2,
      title: 'Project 02',
      imgUrl: '/projects/2.png',
      link: '/project/project-02'
    },
    {
      id: 3,
      title: 'Project 03',
      imgUrl: '/projects/3.png',
      link: '/project/project-03'
    }
  ]);

  private readonly renderCount = 28;
  private readonly mobileRenderCount = 15;
  private readonly spacing = 250;

  private readonly sizes = [
    { width: 640, height: 520 },
    { width: 680, height: 640 },
    { width: 620, height: 560 },
    { width: 700, height: 680 },
    { width: 630, height: 540 },
    { width: 660, height: 620 },
    { width: 640, height: 580 }
  ];

  private readonly desktopCardScale = 1.02;
  private readonly mobileCardScale = 0.62;
  private readonly compactLandscapeCardScale = 0.78;
  private readonly landscapePhoneCardScale = 0.6;

  private readonly carouselView = {
    desktop: {
      scale: 1.0,
      // Растяжение ленты. Меньше - плотнее, больше - шире.
      spread: 0.62,

      // Сдвиг всей карусели.
      shiftX: 0,
      shiftY: 0,
      shiftZ: 0,

      // Глубина траектории. Больше - сильнее перспектива.
      depth: 1,

      // Вертикальный разлёт карточек.
      vertical: 1,

      // Угол карточек.
      rotateY: -20
    },

    mobile: {
      scale: 1.03,
      spread: 0.56,

      shiftX: 0,
      shiftY: 0,
      shiftZ: 0,

      depth: 1,
      vertical: 1,

      rotateY: -16
    }
  };

  private readonly mobileBreakpoint = 768;
  private readonly compactLandscapeMaxWidth = 1366;
  private readonly compactLandscapeMaxHeight = 820;

  private readonly touchInputMedia =
    window.matchMedia('(pointer: coarse)');

  private readonly touchDragThreshold = 8;
  private readonly touchClickBlockDurationMs = 350;

  private touchStartX = 0;
  private touchStartY = 0;
  private touchLastX = 0;
  private isTouchDragging = false;
  private blockClicksUntil = 0;

  private rafId = 0;

  private readonly scrollImpulse = 0.15;
  private readonly scrollFriction = 0.62;
  private readonly scrollMinVelocity = 0.34;

  private currentScroll = 0;
  private scrollVelocity = 0;

  private isScrolling = false;
  private hoverBlockedUntil = 0;

  private lastMouseX = 0;
  private lastMouseY = 0;
  private hasMouse = false;

  private mobileExpandedRenderId: number | null = null;

  private readonly hoverSoundSrc = '/audio/sound.mp3';
  private readonly hoverSoundVolume = 0.16;
  private readonly hoverSoundCooldown = 140;

  private mobileAudioContext: AudioContext | null = null;
  private mobileSoundBuffer: AudioBuffer | null = null;
  private mobileSoundGain: GainNode | null = null;
  private mobileSoundSource: AudioBufferSourceNode | null = null;
  private mobileSoundLoadPromise: Promise<void> | null = null;

  private readonly mobileSoundStartOffset = 0.08;

  private lastPlayedRenderId: number | null = null;
  private lastSoundTime = 0;

  viewport = signal({
    width: window.innerWidth,
    height: window.innerHeight
  });

  scrollSignal = signal(0);
  hoveredRenderId = signal<number | null>(null);
  hoverStrengths = signal<Record<number, number>>({});

  mouseX = signal(0);
  mouseY = signal(0);

  mobileScrollActive = signal(false);

  renderCards = computed<RenderCard[]>(() => {
    const projects = this.projects();

    return Array.from({ length: this.activeRenderCount }, (_, index) => {
      const size = this.sizes[index % this.sizes.length];

      return {
        renderId: index + 1,
        project: projects[index % projects.length],
        width: size.width,
        height: size.height
      };
    });
  });

  hoveredProject = computed(() => {
    const renderId = this.hoveredRenderId();

    if (renderId === null) {
      return null;
    }

    return this.renderCards().find(card => card.renderId === renderId)?.project ?? null;
  });

  isRouteLeaving = signal(false);

  constructor(private router: Router) {}

  private get total(): number {
    return this.spacing * this.activeRenderCount;
  }

  private get activeRenderCount(): number {
    return this.isMobile ? this.mobileRenderCount : this.renderCount;
  }

  private get isMobile(): boolean {
    return this.viewport().width <= this.mobileBreakpoint;
  }

  private get isTouchInput(): boolean {
    return (
      this.touchInputMedia.matches ||
      navigator.maxTouchPoints > 0
    );
  }

  private get isCompactLandscape(): boolean {
    const { width, height } = this.viewport();

    return (
      width > height &&
      width <= this.compactLandscapeMaxWidth &&
      height <= this.compactLandscapeMaxHeight
    );
  }

  private get isLandscapePhone(): boolean {
    const { width, height } = this.viewport();

    return (
      width > height &&
      width <= 932 &&
      height <= 500
    );
  }

  private prepareMobileHoverSound(): Promise<void> {
    if (this.mobileSoundLoadPromise) {
      return this.mobileSoundLoadPromise;
    }

    this.mobileSoundLoadPromise = (async () => {
      const context = new AudioContext({
        latencyHint: 'interactive'
      });

      const gain = context.createGain();

      gain.gain.value = this.hoverSoundVolume;
      gain.connect(context.destination);

      this.mobileAudioContext = context;
      this.mobileSoundGain = gain;

      const response = await fetch(this.hoverSoundSrc);
      const arrayBuffer = await response.arrayBuffer();

      this.mobileSoundBuffer =
        await context.decodeAudioData(arrayBuffer);
    })();

    return this.mobileSoundLoadPromise;
  }

  ngAfterViewInit(): void {
    if (this.isTouchInput) {
      void this.prepareMobileHoverSound();
    }

    this.animate();
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.rafId);
    this.rafId = 0;

    if (this.mobileSoundSource) {
      try {
        this.mobileSoundSource.stop();
      } catch {
      }

      this.mobileSoundSource = null;
    }

    if (this.mobileAudioContext) {
      void this.mobileAudioContext.close();
      this.mobileAudioContext = null;
    }

    this.mobileSoundBuffer = null;
    this.mobileSoundGain = null;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.viewport.set({
      width: window.innerWidth,
      height: window.innerHeight
    });

    this.clearHovered();
    this.hoverBlockedUntil = performance.now() + 180;
  }

  @HostListener('window:wheel', ['$event'])
  onWheel(event: WheelEvent): void {

    event.preventDefault();

    this.scrollVelocity += event.deltaY * this.scrollImpulse;

    this.isScrolling = true;
    this.hoverBlockedUntil = performance.now() + 90;

    this.hoverStrengths.set({});
    this.hoveredRenderId.set(null);
  }

  @HostListener('window:touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    if (!this.isTouchInput || this.isRouteLeaving()) {
      return;
    }
    if (this.mobileAudioContext?.state === 'suspended') {
      void this.mobileAudioContext.resume();
    }

    const touch = event.touches[0];

    if (!touch) {
      return;
    }


    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchLastX = touch.clientX;
    this.isTouchDragging = false;
  }

  @HostListener('window:touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    if (!this.isTouchInput || this.isRouteLeaving()) {
      return;
    }
    const touch = event.touches[0];

    if (!touch) {
      return;
    }

    if (event.cancelable) {
      event.preventDefault();
    }

    const currentX = touch.clientX;
    const currentY = touch.clientY;

    const totalDeltaX = currentX - this.touchStartX;
    const totalDeltaY = currentY - this.touchStartY;
    const totalDistance = Math.hypot(totalDeltaX, totalDeltaY);

    const deltaX = currentX - this.touchLastX;
    this.touchLastX = currentX;

    if (
      !this.isTouchDragging &&
      totalDistance < this.touchDragThreshold
    ) {
      return;
    }

    if (!this.isTouchDragging) {
      this.isTouchDragging = true;
      this.mobileScrollActive.set(true);

      this.blockClicksUntil =
        performance.now() + this.touchClickBlockDurationMs;

      this.resetMobileExpandedCard();
    }

    this.scrollVelocity += deltaX * 0.42;

    this.isScrolling = true;
    this.hoverBlockedUntil = performance.now() + 90;
  }

  @HostListener('window:touchend')
  onTouchEnd(): void {
    if (!this.isTouchInput) {
      return;
    }

    if (this.isTouchDragging) {
      this.blockClicksUntil =
        performance.now() + this.touchClickBlockDurationMs;
    }

    this.isTouchDragging = false;
  }

  @HostListener('window:touchcancel')
  onTouchCancel(): void {
    if (!this.isTouchInput) {
      return;
    }

    this.blockClicksUntil =
      performance.now() + this.touchClickBlockDurationMs;

    this.isTouchDragging = false;
    this.clearHovered();
  }

  private playMobileHoverSound(): void {
    const context = this.mobileAudioContext;
    const buffer = this.mobileSoundBuffer;
    const gain = this.mobileSoundGain;

    if (!context || !buffer || !gain) {
      return;
    }

    const now = performance.now();

    if (now - this.lastSoundTime < this.hoverSoundCooldown) {
      return;
    }

    this.lastSoundTime = now;

    const startSound = (): void => {
      if (this.mobileSoundSource) {
        try {
          this.mobileSoundSource.stop();
        } catch {
          // Предыдущий источник уже остановлен.
        }
      }

      const source = context.createBufferSource();

      source.buffer = buffer;
      source.connect(gain);

      source.start(
        context.currentTime,
        this.mobileSoundStartOffset
      );

      this.mobileSoundSource = source;

      source.onended = () => {
        if (this.mobileSoundSource === source) {
          this.mobileSoundSource = null;
        }
      };
    };

    if (context.state === 'suspended') {
      void context.resume().then(startSound);
      return;
    }

    startSound();
  }

  private animate(): void {
    this.currentScroll += this.scrollVelocity;
    this.scrollVelocity *= this.scrollFriction;

    if (Math.abs(this.scrollVelocity) < this.scrollMinVelocity) {
      this.scrollVelocity = 0;
    }

    this.scrollSignal.set(this.currentScroll);

    const wasScrolling = this.isScrolling;
    this.isScrolling = Math.abs(this.scrollVelocity) > 0;

    if (
      wasScrolling &&
      !this.isScrolling &&
      this.isTouchInput
    ) {
      this.mobileScrollActive.set(false);
    }

    if (this.isScrolling) {
      this.hoveredRenderId.set(null);
    }

    if (
      wasScrolling &&
      !this.isScrolling &&
      this.hasMouse &&
      !this.isTouchInput
    ) {
      requestAnimationFrame(() => {
        this.updateHoverFromLastMouse();
      });
    }

    this.rafId = requestAnimationFrame(() => this.animate());
  }

  onMouseMove(event: MouseEvent): void {
    if (this.isTouchInput) {
      return;
    }

    this.hasMouse = true;

    this.lastMouseX = event.clientX;
    this.lastMouseY = event.clientY;

    this.mouseX.set(event.clientX + 18);
    this.mouseY.set(event.clientY + 14);

    this.updateHoverFromLastMouse();
  }

  private updateHoverFromLastMouse(): void {
    if (
      !this.hasMouse ||
      this.isScrolling ||
      performance.now() < this.hoverBlockedUntil
    ) {
      this.hoverStrengths.set({});
      this.hoveredRenderId.set(null);

      return;
    }

    const nextHoveredId = this.findCardUnderCursor(
      this.lastMouseX,
      this.lastMouseY
    );

    const nextTargets: Record<number, number> = {};

    if (nextHoveredId !== null) {
      nextTargets[nextHoveredId] = 1;
    }

    this.hoverStrengths.set(nextTargets);
    this.hoveredRenderId.set(nextHoveredId);

    if (
      nextHoveredId !== null &&
      nextHoveredId !== this.lastPlayedRenderId
    ) {
      this.playHoverSound();
      this.lastPlayedRenderId = nextHoveredId;
    }

    if (nextHoveredId === null) {
      this.lastPlayedRenderId = null;
    }
  }

  private playHoverSound(): void {
    const now = performance.now();

    if (now - this.lastSoundTime < this.hoverSoundCooldown) {
      return;
    }

    this.lastSoundTime = now;

    const sound = new Audio(this.hoverSoundSrc);

    sound.volume = this.hoverSoundVolume;
    sound.currentTime = 0;

    sound.play().catch(() => {});
  }

  getCardHoverStrength(renderId: number): number {
    return this.hoverStrengths()[renderId] ?? 0;
  }

  private findCardUnderCursor(x: number, y: number): number | null {
    const elements = this.cardHitboxes?.toArray() ?? [];

    let bestRenderId: number | null = null;
    let bestScore = -Infinity;

    const currentHoveredId = this.hoveredRenderId();
    let currentScore = -Infinity;
    let currentStillValid = false;

    for (let i = 0; i < elements.length; i++) {
      if (!this.isCardInActiveScrollZone(i)) {
        continue;
      }

      const hitbox = elements[i].nativeElement;
      const card = hitbox.querySelector('.card') as HTMLElement | null;

      if (!card) {
        continue;
      }

      const hitboxRect = hitbox.getBoundingClientRect();
      const cardRect = card.getBoundingClientRect();

      if (!this.isCardVisible(cardRect)) {
        continue;
      }

      const renderId = Number(hitbox.dataset['renderId']);
      const isCurrentCard = renderId === currentHoveredId;

      const isInside = isCurrentCard
        ? this.isPointInsideHoldArea(x, y, hitboxRect)
        : this.isPointInsideCardArea(x, y, hitboxRect);

      if (!isInside) {
        continue;
      }

      const raw = i * this.spacing - this.scrollSignal();
      const looped = ((raw % this.total) + this.total) % this.total;
      const progress = looped / this.total;

      const centerX = hitboxRect.right - hitboxRect.width * 0.22;
      const centerY = hitboxRect.top + hitboxRect.height * 0.28;

      const distance =
        Math.abs(centerX - x) +
        Math.abs(centerY - y);

      const depthScore = progress * 10000;
      const distanceScore = -distance;

      const score = depthScore + distanceScore;

      if (isCurrentCard) {
        currentScore = score;
        currentStillValid = true;
      }

      if (score > bestScore) {
        bestScore = score;
        bestRenderId = renderId;
      }
    }

    if (
      currentHoveredId !== null &&
      currentStillValid &&
      bestRenderId !== null &&
      bestRenderId !== currentHoveredId
    ) {
      const switchThreshold = 120;

      if (bestScore - currentScore < switchThreshold) {
        return currentHoveredId;
      }
    }

    return bestRenderId;
  }

  private isPointInsideHoldArea(x: number, y: number, rect: DOMRect): boolean {
    return (
      this.isPointInsideTopStrip(x, y, rect, true) ||
      this.isPointInsideRightPart(x, y, rect, true)
    );
  }

  private isPointInsideCardArea(x: number, y: number, rect: DOMRect): boolean {
    return (
      this.isPointInsideTopStrip(x, y, rect, false) ||
      this.isPointInsideRightPart(x, y, rect, false)
    );
  }

  private isPointInsideTopStrip(
    x: number,
    y: number,
    rect: DOMRect,
    isHoldArea: boolean
  ): boolean {
    const left = rect.left + rect.width * 0.08;
    const right = rect.right - rect.width * 0.08;

    const top = rect.top - 12;
    const bottom = isHoldArea
      ? rect.top + rect.height * 0.34
      : rect.top + rect.height * 0.26;

    return (
      x >= left &&
      x <= right &&
      y >= top &&
      y <= bottom
    );
  }

  private isPointInsideRightPart(
    x: number,
    y: number,
    rect: DOMRect,
    isHoldArea: boolean
  ): boolean {
    const visiblePartWidth = isHoldArea
      ? rect.width * 0.62
      : rect.width * 0.46;

    const left = rect.right - visiblePartWidth;
    const right = rect.right;

    const top = rect.top - 8;
    const bottom = isHoldArea
      ? rect.bottom - rect.height * 0.18
      : rect.bottom - rect.height * 0.1;

    return (
      x >= left &&
      x <= right &&
      y >= top &&
      y <= bottom
    );
  }

  private isCardInActiveScrollZone(index: number): boolean {
    const raw = index * this.spacing - this.scrollSignal();
    const looped = ((raw % this.total) + this.total) % this.total;
    const progress = looped / this.total;

    return progress > 0.08 && progress < 0.92;
  }

  private isCardVisible(rect: DOMRect): boolean {
    const minVisibleSize = 100;

    const visibleWidth =
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0);

    const visibleHeight =
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0);

    return visibleWidth > minVisibleSize && visibleHeight > minVisibleSize;
  }

  clearHovered(): void {
    this.hoverStrengths.set({});
    this.hoveredRenderId.set(null);
    this.lastPlayedRenderId = null;
    this.mobileExpandedRenderId = null;
  }

  private resetMobileExpandedCard(): void {
    this.mobileExpandedRenderId = null;

    this.hoveredRenderId.set(null);
    this.hoverStrengths.set({});

    this.lastPlayedRenderId = null;
  }

  openProject(card: RenderCard, event: MouseEvent): void {
    if (this.isRouteLeaving()) {
      return;
    }

    if (
      this.isTouchInput &&
      (
        this.isTouchDragging ||
        this.isScrolling ||
        performance.now() < this.blockClicksUntil
      )
    ) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (this.isTouchInput) {
      const isExpandedCard = this.mobileExpandedRenderId === card.renderId;

      if (!isExpandedCard) {
        event.preventDefault();
        event.stopPropagation();

        this.mobileExpandedRenderId = card.renderId;

        this.hoverStrengths.set({
          [card.renderId]: 1
        });

        this.hoveredRenderId.set(card.renderId);

        if (this.lastPlayedRenderId !== card.renderId) {
          this.playHoverSound();
          this.lastPlayedRenderId = card.renderId;
        }

        return;
      }
    }

    if (!card.project.link) {
      return;
    }

    this.clearHovered();
    this.isRouteLeaving.set(true);

    window.setTimeout(() => {
      this.router.navigateByUrl(card.project.link!);
    }, 75);
  }

  trackByRenderId(_index: number, card: RenderCard): number {
    return card.renderId;
  }

  getTrackTransform(): string {
    const view = this.isMobile
      ? this.carouselView.mobile
      : this.carouselView.desktop;

    return `
    scale(${view.scale})
    rotateX(0deg)
    rotateY(0deg)
    rotateZ(0deg)
  `;
  }

  getCardTransform(index: number): string {
    const view = this.isMobile
      ? this.carouselView.mobile
      : this.carouselView.desktop;

    const raw = index * this.spacing - this.scrollSignal();
    const looped = ((raw % this.total) + this.total) % this.total;
    const progress = looped / this.total;

    const centeredProgress = (progress - 0.5) * view.spread + 0.5;

    const tailProgress = this.isMobile
      ? Math.pow(centeredProgress, 1.04)
      : Math.pow(centeredProgress, 1.24);

    const height = this.getCardHeight(index);
    const heightOffset = this.isMobile
      ? 0
      : (390 - height) * 0.38;

    const desktopBase = {
      x: 2300,
      y: -330,
      z: -3200,

      pathX: 6450,
      pathY: 1420,
      pathZ: 4900
    };

    const mobileBase = {
      x: 1500,
      y: -40,
      z: -2250,

      pathX: 3950,
      pathY: 230,
      pathZ: 4100
    };

    const base = this.isMobile ? mobileBase : desktopBase;

    const x =
      base.x +
      view.shiftX -
      centeredProgress * base.pathX * view.depth;

    const y =
      base.y +
      view.shiftY +
      tailProgress * base.pathY * view.vertical +
      heightOffset;

    const z =
      base.z +
      view.shiftZ +
      tailProgress * base.pathZ * view.depth;

    return `
    translate3d(${x}px, ${y}px, ${z}px)
    rotateX(0deg)
    rotateY(${view.rotateY}deg)
    rotateZ(0deg)
    scale(${view.scale})
  `;
  }

  getCardZIndex(index: number): number {
    const raw = index * this.spacing - this.scrollSignal();
    const looped = ((raw % this.total) + this.total) % this.total;
    const progress = looped / this.total;

    return Math.round(progress * 1000);
  }

  getCardHeight(index: number): number {
    const height = this.renderCards()[index]?.height ?? 520;

    if (this.isMobile) {
      return height * this.mobileCardScale;
    }

    if (this.isLandscapePhone) {
      return height * this.landscapePhoneCardScale;
    }

    if (this.isCompactLandscape) {
      return height * this.compactLandscapeCardScale;
    }

    return height * this.desktopCardScale;
  }

  getCardWidth(index: number): number {
    const width = this.renderCards()[index]?.width ?? 680;

    if (this.isMobile) {
      return width * this.mobileCardScale;
    }

    if (this.isLandscapePhone) {
      return width * this.landscapePhoneCardScale;
    }

    if (this.isCompactLandscape) {
      return width * this.compactLandscapeCardScale;
    }

    return width * this.desktopCardScale;
  }
}
